import type {
  FieldOption,
  FieldType,
  FormField,
  OptionsSource,
} from '@clubezy/form-builder';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  Component,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  model,
  signal,
  Injector,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { boundsInverted, fieldIssue } from './form-issues';

/** Human labels for the field types that can appear in a palette. */
const FIELD_TYPE_LABELS: Partial<Record<FieldType, string>> = {
  text: 'Text',
  textarea: 'Text area',
  number: 'Number',
  email: 'Email',
  phone: 'Phone',
  date: 'Date',
  dob: 'Date of birth',
  select: 'Dropdown',
  radio: 'Radio',
  checkbox: 'Checkbox',
  toggle: 'Yes / No',
  slider: 'Slider',
  rating: 'Rating',
  section: 'Section heading',
  'file-upload': 'File upload',
  address: 'Address',
};

/** Field types that carry a numeric/scored value (scoring metadata applies). */
const SCORABLE: ReadonlySet<FieldType> = new Set<FieldType>([
  'number',
  'slider',
  'rating',
  'toggle',
  'select',
]);

/** Field types whose options the builder can edit. */
const CHOICE_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  'select',
  'radio',
  'checkbox',
]);

/**
 * "Smart" controls — dropdowns whose options come from the club's own data
 * instead of a list the admin retypes into every form. The options are resolved
 * server-side when the form is opened (getPublicRegistrationContext), so the
 * stored schema keeps `options: []` and the static options editor is hidden.
 *
 * Adding a source here is all the builder needs; the backend resolver and the
 * `OptionsSource` union are the other two halves.
 */
const SMART_SOURCES: Record<
  OptionsSource,
  { menuLabel: string; typeLabel: string; label: string; name: string; note: string }
> = {
  seasonTeams: {
    menuLabel: 'Team / age group',
    typeLabel: 'Team picker',
    label: 'Team / age group',
    name: 'ageGroup',
    note: "Options are filled automatically from the season's teams when the registration form is opened.",
  },
  orgPositions: {
    menuLabel: 'Playing position',
    typeLabel: 'Position picker',
    label: 'Preferred position',
    name: 'position',
    note: 'Options are filled automatically from the playing positions set up in Organisation Settings → Positions when the form is opened.',
  },
};

/** Placeholder label for a newly added question, replaced as soon as it's typed. */
const NEW_FIELD_LABEL = 'Untitled question';

/**
 * FormBuilderCanvas — the shared, engine-level building surface used by every
 * form-builder usage (registration, assessment, …). It edits a `FormField[]`
 * model: add fields from a (configurable) palette, edit per-field config,
 * reorder, and — when `showScoring` is on — capture per-field scoring metadata
 * (weighting / inverted / magnitude) so the stats/ranking layer can use it.
 */
@Component({
  selector: 'form-builder-canvas',
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    FormsModule,
    MatButton,
    MatIconButton,
    MatFormField,
    MatHint,
    MatLabel,
    MatIcon,
    MatInput,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatSlideToggle,
    MatTooltip,
  ],
  host: { class: 'block' },
  styles: `
    .cdk-drag-preview {
      box-shadow: 0 8px 24px rgb(0 0 0 / 0.28);
      opacity: 0.95;
    }
    .cdk-drag-placeholder { opacity: 0.35; }
    .cdk-drop-list-dragging .field-row:not(.cdk-drag-placeholder) {
      transition: transform 200ms cubic-bezier(0, 0, 0.2, 1);
    }
    @media (prefers-reduced-motion: reduce) {
      .cdk-drop-list-dragging .field-row:not(.cdk-drag-placeholder) { transition: none; }
    }
  `,
  template: `
    <div class="flex flex-col gap-3">
      @if (controls().length) {
        <div
          class="flex flex-col gap-3"
          cdkDropList
          (cdkDropListDropped)="onDrop($event)"
        >
          @for (field of controls(); track field.id; let i = $index) {
            <div
              class="field-row rounded-lg border bg-white dark:border-neutral-800 dark:bg-neutral-900"
              cdkDrag
              [cdkDragDisabled]="controls().length < 2"
            >
              <!-- Row header -->
              <div class="flex items-center gap-2 p-3">
                <!-- Reorder: drag for mouse, chevrons for keyboard. -->
                <div class="flex shrink-0 items-center gap-0.5">
                  <button
                    matIconButton
                    cdkDragHandle
                    type="button"
                    class="cursor-grab active:cursor-grabbing"
                    [disabled]="controls().length < 2"
                    matTooltip="Drag to reorder"
                    [attr.aria-label]="'Drag ' + fieldName(field) + ' to reorder'"
                  >
                    <mat-icon svgIcon="grip-vertical" class="size-4" />
                  </button>
                  <div class="flex flex-col">
                    <button
                      matIconButton
                      type="button"
                      class="!size-6"
                      matTooltip="Move up"
                      [attr.aria-label]="'Move ' + fieldName(field) + ' up'"
                      [disabled]="i === 0"
                      (click)="move(i, -1)"
                    >
                      <mat-icon svgIcon="chevron-up" class="size-3.5" />
                    </button>
                    <button
                      matIconButton
                      type="button"
                      class="!size-6"
                      matTooltip="Move down"
                      [attr.aria-label]="'Move ' + fieldName(field) + ' down'"
                      [disabled]="i === controls().length - 1"
                      (click)="move(i, 1)"
                    >
                      <mat-icon svgIcon="chevron-down" class="size-3.5" />
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  class="min-w-0 flex-1 cursor-pointer text-left"
                  (click)="toggleExpand(field.id)"
                  [attr.aria-expanded]="expandedId() === field.id"
                >
                  <div class="flex items-center gap-2">
                    <span class="truncate text-sm font-medium">
                      {{ fieldName(field) }}
                    </span>
                    @if (fieldIssue(field); as issue) {
                      <span
                        class="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                        [matTooltip]="issue"
                      >
                        <mat-icon svgIcon="triangle-alert" class="size-3" />
                        Needs attention
                      </span>
                    }
                  </div>
                  <div class="text-xs text-neutral-500">
                    {{ fieldSummary(field) }}
                  </div>
                </button>

                <button
                  matIconButton
                  type="button"
                  matTooltip="Duplicate"
                  [attr.aria-label]="'Duplicate ' + fieldName(field)"
                  (click)="duplicate(field.id)"
                >
                  <mat-icon svgIcon="copy" class="size-4" />
                </button>
                <button
                  matIconButton
                  type="button"
                  [matTooltip]="expandedId() === field.id ? 'Done' : 'Edit'"
                  [attr.aria-label]="'Edit ' + fieldName(field)"
                  (click)="toggleExpand(field.id)"
                >
                  <mat-icon
                    [svgIcon]="expandedId() === field.id ? 'chevron-up' : 'pencil'"
                    class="size-4"
                  />
                </button>
                <button
                  matIconButton
                  type="button"
                  matTooltip="Remove"
                  [attr.aria-label]="'Remove ' + fieldName(field)"
                  class="text-red-600"
                  (click)="remove(field.id)"
                >
                  <mat-icon svgIcon="trash" class="size-4" />
                </button>
              </div>

              <!-- Inline editor -->
              @if (expandedId() === field.id) {
                <div class="border-t p-4 dark:border-neutral-800">
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                      <mat-label>Question</mat-label>
                      <input
                        matInput
                        [attr.id]="labelInputId(field.id)"
                        [ngModel]="field.label"
                        (ngModelChange)="patch(field.id, { label: $event })"
                        placeholder="What are you asking?"
                      />
                    </mat-form-field>

                    @if (field.type !== 'section') {
                      <div class="flex items-center">
                        <mat-slide-toggle
                          [ngModel]="field.required ?? false"
                          (ngModelChange)="patch(field.id, { required: $event })"
                        >
                          Required
                        </mat-slide-toggle>
                      </div>
                    }

                    <mat-form-field
                      appearance="outline"
                      subscriptSizing="dynamic"
                      class="sm:col-span-2"
                    >
                      <mat-label>Help text (optional)</mat-label>
                      <input
                        matInput
                        [ngModel]="field.description"
                        (ngModelChange)="patch(field.id, { description: $event })"
                      />
                    </mat-form-field>

                    <!-- Slider bounds -->
                    @if (field.type === 'slider') {
                      <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>Min</mat-label>
                        <input
                          matInput
                          type="number"
                          [ngModel]="numProp(field, 'min') ?? 0"
                          (ngModelChange)="patch(field.id, { min: +$event })"
                        />
                      </mat-form-field>
                      <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>Max</mat-label>
                        <input
                          matInput
                          type="number"
                          [ngModel]="numProp(field, 'max') ?? 10"
                          (ngModelChange)="patch(field.id, { max: +$event })"
                        />
                      </mat-form-field>
                      <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>Step</mat-label>
                        <input
                          matInput
                          type="number"
                          [ngModel]="numProp(field, 'step') ?? 1"
                          (ngModelChange)="patch(field.id, { step: +$event })"
                        />
                      </mat-form-field>
                    }

                    <!-- Date of birth: an AGE rule, not a date. Stored as the
                         rule so it never needs revisiting — the picker works
                         out the dates against the day the form is opened.
                         (CLUB-2003) -->
                    @if (field.type === 'dob') {
                      <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>Minimum age (optional)</mat-label>
                        <input
                          matInput
                          type="number"
                          min="0"
                          placeholder="Any age"
                          [ngModel]="numProp(field, 'minimumAge')"
                          (ngModelChange)="patchAge(field.id, 'minimumAge', $event)"
                        />
                        <mat-hint>Future dates are blocked whether or not you set this</mat-hint>
                      </mat-form-field>
                      <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>Maximum age (optional)</mat-label>
                        <input
                          matInput
                          type="number"
                          min="0"
                          placeholder="No limit"
                          [ngModel]="numProp(field, 'maximumAge')"
                          (ngModelChange)="patchAge(field.id, 'maximumAge', $event)"
                        />
                      </mat-form-field>
                    }

                    <!-- Rating stars -->
                    @if (field.type === 'rating') {
                      <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>Max stars</mat-label>
                        <input
                          matInput
                          type="number"
                          [ngModel]="numProp(field, 'maxStars') ?? 5"
                          (ngModelChange)="patch(field.id, { maxStars: +$event })"
                        />
                      </mat-form-field>
                    }

                    <!-- Number bounds -->
                    @if (field.type === 'number') {
                      <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>Min</mat-label>
                        <input
                          matInput
                          type="number"
                          [ngModel]="field.validation?.min"
                          (ngModelChange)="patchValidation(field.id, { min: numberOrUndefined($event) })"
                        />
                      </mat-form-field>
                      <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>Max</mat-label>
                        <input
                          matInput
                          type="number"
                          [ngModel]="field.validation?.max"
                          (ngModelChange)="patchValidation(field.id, { max: numberOrUndefined($event) })"
                        />
                        @if (boundsInverted(field)) {
                          <mat-hint class="!text-amber-700 dark:!text-amber-400">
                            <span>Max is below Min, so nothing will be accepted.</span>
                          </mat-hint>
                        }
                      </mat-form-field>
                    }
                  </div>

                  <!-- Options editor for choice fields (static options only) -->
                  @if (isChoice(field.type) && !fieldSource(field)) {
                    <div class="mt-5">
                      <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <span class="text-sm font-medium">
                          Options
                          <span class="ml-1 font-normal text-neutral-500">
                            ({{ options(field).length }})
                          </span>
                        </span>
                        <div class="flex items-center gap-2">
                          <button
                            matButton="text"
                            type="button"
                            class="gap-1.5"
                            (click)="toggleBulk(field.id)"
                          >
                            <mat-icon svgIcon="list" class="size-4" />
                            {{ bulkFor() === field.id ? 'Close paste box' : 'Paste a list' }}
                          </button>
                          <button
                            matButton="tonal"
                            type="button"
                            class="gap-1.5"
                            (click)="addOption(field.id)"
                          >
                            <mat-icon svgIcon="plus" class="size-4" />
                            Add option
                          </button>
                        </div>
                      </div>

                      @if (bulkFor() === field.id) {
                        <div
                          class="mb-3 rounded-lg border border-dashed p-3 dark:border-neutral-700"
                        >
                          <mat-form-field
                            appearance="outline"
                            subscriptSizing="dynamic"
                            class="w-full"
                          >
                            <mat-label>One option per line</mat-label>
                            <textarea
                              matInput
                              rows="5"
                              [ngModel]="bulkText()"
                              (ngModelChange)="bulkText.set($event)"
                              placeholder="Under 8&#10;Under 10&#10;Under 12"
                            ></textarea>
                          </mat-form-field>
                          <div class="mt-2 flex items-center gap-2">
                            <button
                              matButton="filled"
                              type="button"
                              [disabled]="!bulkLineCount()"
                              (click)="applyBulk(field.id, false)"
                            >
                              Add {{ bulkLineCount() }} option{{ bulkLineCount() === 1 ? '' : 's' }}
                            </button>
                            <button
                              matButton="text"
                              type="button"
                              [disabled]="!bulkLineCount()"
                              (click)="applyBulk(field.id, true)"
                            >
                              Replace all
                            </button>
                          </div>
                        </div>
                      }

                      @if (!options(field).length) {
                        <p
                          class="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                        >
                          No options yet — nobody can answer this question until you add some.
                        </p>
                      }

                      @for (opt of options(field); track $index; let oi = $index) {
                        <div class="mb-2 flex items-center gap-1">
                          <span
                            class="w-6 shrink-0 text-right text-xs tabular-nums text-neutral-400"
                          >
                            {{ oi + 1 }}.
                          </span>
                          <mat-form-field
                            appearance="outline"
                            subscriptSizing="dynamic"
                            class="flex-1"
                          >
                            <input
                              matInput
                              [attr.aria-label]="'Option ' + (oi + 1)"
                              [ngModel]="opt.label"
                              (ngModelChange)="updateOption(field.id, oi, $event)"
                              (keydown.enter)="onOptionEnter($event, field.id, oi)"
                            />
                          </mat-form-field>
                          <button
                            matIconButton
                            type="button"
                            class="!size-8"
                            matTooltip="Move up"
                            [attr.aria-label]="'Move option ' + (oi + 1) + ' up'"
                            [disabled]="oi === 0"
                            (click)="moveOption(field.id, oi, -1)"
                          >
                            <mat-icon svgIcon="chevron-up" class="size-4" />
                          </button>
                          <button
                            matIconButton
                            type="button"
                            class="!size-8"
                            matTooltip="Move down"
                            [attr.aria-label]="'Move option ' + (oi + 1) + ' down'"
                            [disabled]="oi === options(field).length - 1"
                            (click)="moveOption(field.id, oi, 1)"
                          >
                            <mat-icon svgIcon="chevron-down" class="size-4" />
                          </button>
                          <button
                            matIconButton
                            type="button"
                            class="!size-8 text-red-600"
                            matTooltip="Remove option"
                            [attr.aria-label]="'Remove option ' + (oi + 1)"
                            (click)="removeOption(field.id, oi)"
                          >
                            <mat-icon svgIcon="trash" class="size-4" />
                          </button>
                        </div>
                      }
                    </div>
                  } @else if (fieldSource(field); as source) {
                    <div
                      class="mt-4 rounded-lg bg-neutral-50 p-3 text-sm text-neutral-600 dark:bg-neutral-800/40 dark:text-neutral-300"
                    >
                      {{ smartSource(source).note }}
                    </div>
                  }

                  <!-- Scoring metadata -->
                  @if (isScorable(field.type)) {
                    <div class="mt-4 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-800/40">
                      <div class="mb-2 text-sm font-medium">Scoring</div>
                      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                          <mat-label>Weighting (0–1)</mat-label>
                          <input
                            matInput
                            type="number"
                            min="0"
                            max="1"
                            step="0.1"
                            [ngModel]="asScoring(field)?.weighting ?? 1"
                            (ngModelChange)="patchScoring(field.id, { weighting: +$event })"
                          />
                        </mat-form-field>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                          <mat-label>Unit (optional)</mat-label>
                          <input
                            matInput
                            [ngModel]="asScoring(field)?.magnitude"
                            (ngModelChange)="patchScoring(field.id, { magnitude: $event })"
                          />
                        </mat-form-field>
                        <div class="flex items-center">
                          <mat-slide-toggle
                            [ngModel]="asScoring(field)?.inverted ?? false"
                            (ngModelChange)="patchScoring(field.id, { inverted: $event })"
                          >
                            Lower is better
                          </mat-slide-toggle>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      } @else {
        <p class="text-sm text-neutral-500">
          No questions yet. Add one from the palette below to start building.
        </p>
      }

      <!-- Add-field palette -->
      <div>
        <button
          matButton="tonal"
          type="button"
          class="gap-1.5"
          [matMenuTriggerFor]="paletteMenu"
        >
          <mat-icon svgIcon="plus" class="size-4" />
          Add question
        </button>
        <mat-menu #paletteMenu="matMenu">
          @for (type of palette(); track type) {
            <button mat-menu-item type="button" (click)="addField(type)">
              {{ typeLabel(type) }}
            </button>
          }
          @if (smartSources().length) {
            <div
              class="px-4 pb-1 pt-3 text-xs font-medium uppercase tracking-wider text-neutral-500"
            >
              From your club
            </div>
            @for (source of smartSources(); track source) {
              <button mat-menu-item type="button" (click)="addSmartField(source)">
                {{ smartSource(source).menuLabel }}
              </button>
            }
          }
        </mat-menu>
      </div>
    </div>
  `,
})
export class FormBuilderCanvas {
  /** The field list being edited (two-way bindable). */
  readonly controls = model<FormField[]>([]);
  /** Field types offered in the add-field palette. */
  readonly palette = input<FieldType[]>([
    'text',
    'textarea',
    'number',
    'select',
    'date',
    'dob',
    'toggle',
  ]);
  /** Show per-field scoring metadata (weighting / inverted / unit). */
  readonly showScoring = input(false);

  /**
   * Smart option sources offered in the palette — dropdowns filled from club
   * data at render time rather than from a static list (`optionsSource`).
   * Registration forms only. (CLUB-1613)
   */
  readonly smartSources = input<OptionsSource[]>([]);

  private readonly hostRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly injector = inject(Injector);
  private readonly snackBar = inject(MatSnackBar);

  protected readonly expandedId = signal<string | null>(null);

  /**
   * Field whose bulk-paste box is open, and its text.
   *
   * `bulkText` must be a signal, not a plain property: `bulkLineCount` is a
   * computed, and the app runs zoneless — a plain field would neither be
   * tracked by the computed nor trigger change detection, so the button sat on
   * "Add 0 options" no matter what was pasted.
   */
  protected readonly bulkFor = signal<string | null>(null);
  protected readonly bulkText = signal('');

  protected readonly bulkLineCount = computed(() => this.bulkLines().length);

  protected typeLabel(type: FieldType): string {
    return FIELD_TYPE_LABELS[type] ?? type;
  }

  /**
   * The type shown on a field's collapsed row. Smart controls are stored as a
   * plain `select`, so without this a team or position picker read as
   * "Dropdown" — and then looked broken, because its options list is empty by
   * design until the form is opened.
   */
  protected fieldTypeLabel(field: FormField): string {
    const source = this.fieldSource(field);
    return source ? SMART_SOURCES[source].typeLabel : this.typeLabel(field.type);
  }

  /** Display name for a field in the row and in aria labels. */
  protected fieldName(field: FormField): string {
    return field.label || field.name || NEW_FIELD_LABEL;
  }

  /** One-line summary under the field name: type, required, options. */
  protected fieldSummary(field: FormField): string {
    const parts = [this.fieldTypeLabel(field)];
    if (field.required) parts.push('required');
    if (this.isChoice(field.type)) {
      if (this.fieldSource(field)) {
        parts.push('filled from your club');
      } else {
        const count = this.options(field).length;
        parts.push(`${count} option${count === 1 ? '' : 's'}`);
      }
    }
    const scoring = this.isScorable(field.type) ? this.asScoring(field) : null;
    if (scoring) {
      parts.push(`weight ${scoring.weighting ?? 1}`);
      if (scoring.inverted) parts.push('lower is better');
    }
    return parts.join(' · ');
  }

  /**
   * A problem that would reach applicants, surfaced on the collapsed row so a
   * long form can be audited without opening every field. Shared with the form
   * editor, which gates Activate on the same checks — the warning you see and
   * the rule that stops you publishing must not disagree.
   */
  protected fieldIssue(field: FormField): string | null {
    return fieldIssue(field);
  }

  /** True when a number field's Max is below its Min. */
  protected boundsInverted(field: FormField): boolean {
    return boundsInverted(field);
  }

  /** Empty number input → undefined rather than 0. */
  protected numberOrUndefined(value: unknown): number | undefined {
    if (value === '' || value === null || value === undefined) return undefined;
    const n = Number(value);
    return Number.isNaN(n) ? undefined : n;
  }

  protected isChoice(type: FieldType): boolean {
    return CHOICE_TYPES.has(type);
  }

  protected isScorable(type: FieldType): boolean {
    return this.showScoring() && SCORABLE.has(type);
  }

  /** Untyped view of a field for template access to variant-specific props. */
  protected asAny(field: FormField): Record<string, unknown> {
    return field as unknown as Record<string, unknown>;
  }

  /** Typed numeric accessor for variant-specific props (slider/rating bounds). */
  protected numProp(field: FormField, key: string): number | undefined {
    const value = (field as unknown as Record<string, unknown>)[key];
    return typeof value === 'number' ? value : undefined;
  }

  /**
   * Set or clear a date-of-birth age bound.
   *
   * Clearing the box must REMOVE the key, not store 0 or NaN: `minimumAge: 0`
   * is a real rule that happens to equal the default, and NaN would be carried
   * into the saved schema. `+''` is 0 and `+'abc'` is NaN, so the usual
   * `+$event` the numeric editors above use would do exactly that here.
   */
  protected patchAge(
    fieldId: string,
    key: 'minimumAge' | 'maximumAge',
    value: unknown,
  ): void {
    const text = `${value ?? ''}`.trim();
    const parsed = text === '' ? null : Number(text);
    const age =
      parsed !== null && Number.isInteger(parsed) && parsed >= 0 && parsed < 200 ?
        parsed :
        undefined;
    this.patch(fieldId, { [key]: age } as Partial<FormField>);
  }

  /** Options for a choice field (empty when absent). */
  protected options(field: FormField): FieldOption[] {
    const value = (field as unknown as Record<string, unknown>)['options'];
    return Array.isArray(value) ? (value as FieldOption[]) : [];
  }

  /** Scoring block accessor (returns undefined when absent). */
  protected asScoring(field: FormField) {
    return (field as { scoring?: { weighting?: number; inverted?: boolean; magnitude?: string } })
      .scoring;
  }

  /** Config for a smart source, exposed to the template. */
  protected smartSource(source: OptionsSource) {
    return SMART_SOURCES[source];
  }

  /** The smart source a field draws its options from, if any. */
  protected fieldSource(field: FormField): OptionsSource | null {
    const source = this.asAny(field)['optionsSource'];
    return typeof source === 'string' && source in SMART_SOURCES ?
      (source as OptionsSource) :
      null;
  }

  protected toggleExpand(id: string): void {
    this.expandedId.update((cur) => (cur === id ? null : id));
    if (this.bulkFor() !== id) this.closeBulk();
  }

  protected labelInputId(id: string): string {
    return `fb-label-${id}`;
  }

  // ─── Add / duplicate / remove ────────────────────────────────────

  protected addField(type: FieldType): void {
    const field: Record<string, unknown> = {
      id: crypto.randomUUID(),
      type,
      // A placeholder rather than "Yes / No 5": the old label numbered the
      // field by its position in the WHOLE list, which meant nothing to the
      // author and was wrong the moment anything was deleted or reordered.
      label: NEW_FIELD_LABEL,
      name: this.freshName(),
      required: false,
    };
    if (type === 'slider') Object.assign(field, { min: 0, max: 10, step: 1 });
    if (type === 'rating') Object.assign(field, { maxStars: 5 });
    if (CHOICE_TYPES.has(type)) Object.assign(field, { options: [] as FieldOption[] });
    if (this.isScorable(type)) {
      field['scoring'] = { weighting: 1, inverted: false };
    }
    this.appendAndOpen(field as unknown as FormField);
  }

  /**
   * Add a smart control — a `select` whose options are resolved from club data
   * at render time (server-side, via optionsSource, CLUB-1613). The static
   * options editor is hidden for it.
   */
  protected addSmartField(source: OptionsSource): void {
    const config = SMART_SOURCES[source];
    this.appendAndOpen({
      id: crypto.randomUUID(),
      type: 'select',
      label: config.label,
      name: config.name,
      required: true,
      options: [] as FieldOption[],
      optionsSource: source,
    } as unknown as FormField);
  }

  /** Copy a field, including its options, and drop it directly below. */
  protected duplicate(id: string): void {
    const list = this.controls();
    const index = list.findIndex((f) => f.id === id);
    if (index < 0) return;

    const source = list[index] as unknown as Record<string, unknown>;
    const copy: Record<string, unknown> = {
      ...source,
      id: crypto.randomUUID(),
      name: this.freshName(),
      label: `${source['label'] || NEW_FIELD_LABEL} (copy)`,
    };
    // Options are plain {label,value} pairs — clone the array so editing the
    // copy doesn't reach back into the original.
    if (Array.isArray(source['options'])) {
      copy['options'] = (source['options'] as FieldOption[]).map((o) => ({ ...o }));
    }
    if (source['scoring']) copy['scoring'] = { ...(source['scoring'] as object) };

    const created = copy as unknown as FormField;
    this.controls.update((c) => [...c.slice(0, index + 1), created, ...c.slice(index + 1)]);
    this.expandedId.set(created.id);
    this.focusLabel(created.id);
  }

  /**
   * Remove a field, offering an undo. A question can carry a dozen options and
   * its own validation, and a single click on the bin used to destroy all of it
   * with no way back.
   */
  protected remove(id: string): void {
    const list = this.controls();
    const index = list.findIndex((f) => f.id === id);
    if (index < 0) return;
    const removed = list[index];

    this.controls.update((c) => c.filter((f) => f.id !== id));
    if (this.expandedId() === id) this.expandedId.set(null);
    if (this.bulkFor() === id) this.closeBulk();

    this.snackBar
      .open(`Removed "${this.fieldName(removed)}"`, 'Undo', { duration: 8000 })
      .onAction()
      .subscribe(() => {
        this.controls.update((c) => {
          if (c.some((f) => f.id === removed.id)) return c;
          const at = Math.min(index, c.length);
          return [...c.slice(0, at), removed, ...c.slice(at)];
        });
        this.expandedId.set(removed.id);
      });
  }

  private appendAndOpen(created: FormField): void {
    this.controls.update((c) => [...c, created]);
    this.expandedId.set(created.id);
    this.focusLabel(created.id);
  }

  /**
   * Put the caret in the new question's wording box.
   *
   * Without this a new field lands at the bottom of the list, below the fold,
   * and the author has to scroll to find what they just added — every time.
   */
  private focusLabel(id: string): void {
    afterNextRender(
      () => {
        const input = this.hostRef.nativeElement.querySelector<HTMLInputElement>(
          `#${CSS.escape(this.labelInputId(id))}`
        );
        if (!input) return;
        if (typeof input.scrollIntoView === 'function') {
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        input.focus({ preventScroll: true });
        input.select?.();
      },
      { injector: this.injector }
    );
  }

  /**
   * A unique control name. Nothing in the registration flow reads `name` for a
   * custom question (answers are keyed by id), but the profile-section dedupe
   * and the team-picker fallback both match on it, so it must not collide —
   * which a label-derived slug did as soon as two questions shared wording.
   */
  private freshName(): string {
    return `field_${crypto.randomUUID().slice(0, 8)}`;
  }

  // ─── Field edits ─────────────────────────────────────────────────

  protected patch(id: string, patch: Record<string, unknown>): void {
    this.controls.update((c) =>
      c.map((f) => (f.id === id ? ({ ...f, ...patch } as FormField) : f))
    );
  }

  protected patchValidation(id: string, patch: Record<string, unknown>): void {
    this.controls.update((c) =>
      c.map((f) =>
        f.id === id
          ? ({ ...f, validation: { ...(f.validation ?? {}), ...patch } } as FormField)
          : f
      )
    );
  }

  protected patchScoring(id: string, patch: Record<string, unknown>): void {
    this.controls.update((c) =>
      c.map((f) => {
        if (f.id !== id) return f;
        const scoring = { ...(this.asScoring(f) ?? {}), ...patch };
        return { ...f, scoring } as FormField;
      })
    );
  }

  // ─── Reordering ──────────────────────────────────────────────────

  protected onDrop(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex === event.currentIndex) return;
    this.controls.update((c) => {
      const next = [...c];
      moveItemInArray(next, event.previousIndex, event.currentIndex);
      return next;
    });
  }

  protected move(index: number, dir: -1 | 1): void {
    const target = index + dir;
    this.controls.update((c) => {
      if (target < 0 || target >= c.length) return c;
      const next = [...c];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  // ─── Choice options ──────────────────────────────────────────────

  protected addOption(id: string): void {
    this.updateOptions(id, (options) => [
      ...options,
      { label: '', value: this.freshOptionValue(options) },
    ]);
  }

  /**
   * Rename an option WITHOUT touching its stored value.
   *
   * The value used to be re-derived from the label on every keystroke, so
   * correcting a typo in "Midfeilder" a month into the season silently orphaned
   * every answer already submitted against the old slug — the reviewer then saw
   * a raw value with no matching option. Values are minted once, on create.
   */
  protected updateOption(id: string, index: number, label: string): void {
    this.updateOptions(id, (options) =>
      options.map((o, i) => (i === index ? { ...o, label } : o))
    );
  }

  protected removeOption(id: string, index: number): void {
    this.updateOptions(id, (options) => options.filter((_, i) => i !== index));
  }

  protected moveOption(id: string, index: number, dir: -1 | 1): void {
    const target = index + dir;
    this.updateOptions(id, (options) => {
      if (target < 0 || target >= options.length) return options;
      const next = [...options];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  /** Enter in an option box adds the next one, so a list can be typed straight through. */
  protected onOptionEnter(event: Event, id: string, index: number): void {
    event.preventDefault();
    const field = this.controls().find((f) => f.id === id);
    if (!field) return;
    if (index === this.options(field).length - 1) this.addOption(id);
  }

  // ─── Bulk option entry ───────────────────────────────────────────

  protected toggleBulk(id: string): void {
    if (this.bulkFor() === id) {
      this.closeBulk();
      return;
    }
    this.bulkFor.set(id);
    this.bulkText.set('');
  }

  private closeBulk(): void {
    this.bulkFor.set(null);
    this.bulkText.set('');
  }

  private bulkLines(): string[] {
    return this.bulkText()
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  }

  /**
   * Turn a pasted list into options. Entering a dozen age groups one click at a
   * time was the slowest thing in the builder.
   */
  protected applyBulk(id: string, replace: boolean): void {
    const lines = this.bulkLines();
    if (!lines.length) return;

    this.updateOptions(id, (existing) => {
      const base = replace ? [] : existing;
      const next = [...base];
      for (const label of lines) {
        next.push({ label, value: this.freshOptionValue(next) });
      }
      return next;
    });

    const added = lines.length;
    this.closeBulk();
    this.snackBar.open(
      replace ?
        `Replaced with ${added} option${added === 1 ? '' : 's'}` :
        `Added ${added} option${added === 1 ? '' : 's'}`,
      'Dismiss',
      { duration: 3000 }
    );
  }

  // ─── Option helpers ──────────────────────────────────────────────

  private updateOptions(
    id: string,
    change: (options: FieldOption[]) => FieldOption[]
  ): void {
    this.controls.update((c) =>
      c.map((f) => {
        if (f.id !== id) return f;
        return { ...f, options: change(this.options(f)) } as FormField;
      })
    );
  }

  /**
   * A stable, unique option value. Numbering from `options.length` collided
   * after a delete (remove option 2 of 3, add another, and it reused
   * `option_3`), which quietly merged two different answers.
   */
  private freshOptionValue(existing: FieldOption[]): string {
    const taken = new Set(existing.map((o) => o.value));
    let n = existing.length + 1;
    let value = `option_${n}`;
    while (taken.has(value)) {
      n += 1;
      value = `option_${n}`;
    }
    return value;
  }
}
